import React from "react";
import { Box } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink";
import { useTranslation } from "../../localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { annualWheelActivityIcon, obligationIcon } from "@common/icons.tsx";
import { ContractOverviewTabEnum } from "../../contract/contractOverview/ContractOverview.types";
import {
    EvaluationStatus,
    getEvaluationIcon,
} from "@common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import { AnnualWheelPriority } from "../../annualWheel/annualWheelDialog/AnnualWheelDialog.types";
import { getRiskIcon } from "./Task";
import { useLocation } from "react-router";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import { useContentSize } from "../../main/content/ContentSizeContext.tsx";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";

interface TaskHeaderProps {
    activityName: string;
    customId?: string;
    taskId: string;
    evaluationStatus?: EvaluationStatus;
    evaluation?: string;
    isAdhocTask: boolean;
    contractId?: string;
    contractName?: string;
    contractUserFriendlyId?: number;
    annualWheelActivityId?: string;
    priority: AnnualWheelPriority;
    riskMitigationPlanId?: string | null;
}

const TaskHeader: React.FC<TaskHeaderProps> = ({
    activityName,
    customId,
    taskId,
    evaluationStatus,
    evaluation,
    isAdhocTask,
    contractId,
    contractName,
    contractUserFriendlyId,
    annualWheelActivityId,
    priority,
    riskMitigationPlanId,
}) => {
    const { translateString } = useTranslation();
    const { isOnMediumScreen } = useContentSize();
    const { getContractOverviewUrl, getAnnualWheelUrlWithActivityId } = useUrlProvider();
    const location = useLocation();
    const { permissions } = useUserContext();
    const { hasAnnualWheelActivityAccess } = usePermissionsProvider();

    // The same rule the server applies in EnsureCanReadActivity: an obligation is read through its contract.
    const canReadActivity = contractId ? permissions.contractManagementPermissions.permissions.read : hasAnnualWheelActivityAccess();
    const activityIcon = contractId ? (
        <DotLegalTooltip text={translateString("connectedToObligation")}>{obligationIcon}</DotLegalTooltip>
    ) : (
        <DotLegalTooltip text={translateString("connectedToAnnualWheelActivity")}>{annualWheelActivityIcon}</DotLegalTooltip>
    );

    const getTaskLinkUrl = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("taskId", taskId);
        return `?${searchParams.toString()}`;
    };

    const topRightIconWrapper = {
        "& svg": {
            width: 14,
            height: 14,
        },
        display: "flex",
        alignItems: "center",
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginRight: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box sx={{ fontSize: "14px" }}>
                    {customId && (
                        <Box component={"span"} sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold, color: theme.palette.primary.dark })}>
                            {customId}{" "}
                        </Box>
                    )}
                    <DotLegalLink to={getTaskLinkUrl()} linkColor={"primary-dark"}>
                        {riskMitigationPlanId && (
                            <Box component={"span"} sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
                                {translateString("riskMitigationTaskLabel")}:{" "}
                            </Box>
                        )}
                        {activityName}
                    </DotLegalLink>
                    {contractName && (
                        <>
                            {" "}
                            -{" "}
                            <DotLegalLink to={getContractOverviewUrl(contractId!, ContractOverviewTabEnum.Obligation)} bold linkColor={"primary"}>
                                {`${contractUserFriendlyId}: ${contractName}`}
                            </DotLegalLink>
                        </>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: !isOnMediumScreen ? "flex" : "none", gap: 0.75 }}>
                {evaluationStatus && (
                    <DotLegalTooltip text={<MentionInputRender value={evaluation ?? ""} />}>
                        <Box sx={topRightIconWrapper}>{getEvaluationIcon(evaluationStatus)}</Box>
                    </DotLegalTooltip>
                )}
                {(!isAdhocTask || contractId) && (
                    <Box sx={topRightIconWrapper}>
                        {/* Without read access the target page answers 403, which sends the user to the forbidden
                            page, so the icon keeps only its tooltip. */}
                        {!canReadActivity ? (
                            activityIcon
                        ) : contractId ? (
                            <DotLegalLink to={getContractOverviewUrl(contractId, ContractOverviewTabEnum.Obligation)}>{activityIcon}</DotLegalLink>
                        ) : (
                            <DotLegalLink to={getAnnualWheelUrlWithActivityId(annualWheelActivityId!)}>{activityIcon}</DotLegalLink>
                        )}
                    </Box>
                )}
                <Box sx={topRightIconWrapper}>{getRiskIcon(priority, translateString)}</Box>
            </Box>
        </Box>
    );
};

export default TaskHeader;
